import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Card, Col, Row } from 'react-bootstrap';
import { userService } from '../../services/apiService';
import { useAuthToken } from '../../hooks/useAuthToken';
import { LoadingState, ErrorState, serverMessage } from '../PageState';
import { formatDate } from '../../utils/format';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const on = (note) => ({ on: true, note });
const off = (note) => ({ on: false, note });

/*
 * Each notification's state, following the main API's rules:
 * - Reminder emails: WhereaboutsReminderChecker and WhereaboutsReminderService
 * - Leave emails and Teams messages: the holidaysV2 *NotificationService classes
 * - Teams messages: TeamsNotificationService and TeamsWhereaboutsReminderChecker
 * - Any email: EmailService skips addresses on the suppression list
 */
const emailNotifications = (email, inCompany) => {
  const blocked = email.deleted ? 'User is deleted'
    : email.suppression ? 'This address is blocked'
    : !inCompany ? 'Not in a company'
    : null;

  let reminders;
  if (blocked) reminders = off(blocked);
  else if (email.companyReminderSetting === 'NO_USERS') reminders = off('Switched off for the whole company');
  else if (!email.companyPaidOrFreeTrial) reminders = off('Only sent on paid plans and free trials');
  else if (!email.whereaboutsReminders) reminders = off('Switched off for this user, in their settings or by an unsubscribe link');
  else if (!email.verified) reminders = off('Email address not verified');
  else if (!email.accessPermitted) reminders = off('User has no access');
  else reminders = on('Thursday to Saturday when next week has gaps, at most once every 5 days');

  let leave;
  if (blocked) leave = off(blocked);
  else if (!email.leaveNotificationsEnabled) leave = off('Switched off for the whole company');
  else leave = on('Leave approved, declined or cancelled, and requests to approve');

  return [
    { label: 'Whereabouts reminders', ...reminders },
    { label: 'Leave updates', ...leave },
    { label: 'Marketing emails', on: email.marketingEmails, note: null },
  ];
};

const teamsNotifications = (teams, email, inCompany) => {
  const blocked = email.deleted ? 'User is deleted'
    : !teams.chatLinked ? 'No chat with the Team Today app'
    : teams.notificationsDisabled ? 'Teams notifications switched off for this user'
    : !inCompany ? 'Not in a company'
    : null;

  const { reminder } = teams;
  let reminders;
  if (blocked) reminders = off(blocked);
  else if (!reminder) reminders = off('The company has no Teams reminder set up');
  else if (!reminder.enabled) reminders = off('Switched off for the whole company');
  else if (!reminder.timezoneId) reminders = off("The company's reminder has no time zone, so it doesn't run");
  else if (!email.accessPermitted) reminders = off('User has no access');
  else {
    const day = DAYS[(reminder.dayOfWeek ?? 1) - 1] || DAYS[0];
    const hour = String(reminder.hourOfDay ?? 0).padStart(2, '0');
    reminders = on(`${day}s at ${hour}:00 (${reminder.timezoneId}) when their week has gaps`);
  }

  let leave;
  if (blocked) leave = off(blocked);
  else if (!email.leaveNotificationsEnabled) leave = off('Switched off for the whole company');
  else leave = on('Leave approved or declined, and requests to approve');

  return [
    { label: 'Whereabouts reminders', ...reminders },
    { label: 'Leave updates', ...leave },
  ];
};

const OnOff = ({ value, labels = ['On', 'Off'] }) => (
  <Badge bg={value ? 'success' : 'secondary'}>{value ? labels[0] : labels[1]}</Badge>
);

const Note = ({ children }) => (children ? <div className="small text-muted mt-1">{children}</div> : null);

const NotificationList = ({ items }) => (
  <dl className="kv-list">
    {items.map((item) => (
      <React.Fragment key={item.label}>
        <dt>{item.label}</dt>
        <dd>
          <OnOff value={item.on} labels={item.labels} />
          <Note>{item.note}</Note>
        </dd>
      </React.Fragment>
    ))}
  </dl>
);

const summary = (emailOn, teamsOn) => {
  if (emailOn && teamsOn) return 'Gets email and Teams notifications';
  if (emailOn) return 'Gets email notifications only';
  if (teamsOn) return 'Gets Teams notifications only';
  return 'Gets no notifications';
};

/**
 * Which emails and Teams messages Team Today sends this user, with the reason for each one that is
 * off. Read only: users change their own switches in Personal preferences.
 */
const UserNotifications = ({ user }) => {
  const refreshToken = useAuthToken();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await refreshToken();
      const response = await userService.getUserNotifications(user.id);
      setData(response.data);
    } catch (err) {
      setError(err);
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [refreshToken, user.id]);

  // Saving the user can change their company, access or verified address, which all count here
  useEffect(() => {
    load();
  }, [load, user.companyId, user.accessPermitted, user.emailVerified, user.email]);

  let body;
  let headerNote = null;
  if (loading && !data) {
    body = <LoadingState label="Loading notifications" />;
  } else if (error) {
    body = <ErrorState message={serverMessage(error, 'Could not load notifications.')} onRetry={load} inCard />;
  } else {
    const { email, teams } = data;
    const inCompany = !!user.companyId;
    const emailItems = emailNotifications(email, inCompany);
    const teamsItems = teamsNotifications(teams, email, inCompany);
    headerNote = summary(emailItems.some((item) => item.on), teamsItems.some((item) => item.on));

    const { suppression } = email;
    const suppressionText = suppression && (
      `Team Today stopped emailing this address: ${suppression.reason === 'COMPLAINT'
        ? 'they marked an email as spam'
        : 'it bounced'}${suppression.lastSeenAt ? ` on ${formatDate(suppression.lastSeenAt)}` : ''}.`
    );

    let chatNote;
    if (teams.chatLinked) chatNote = teams.chatLinkedAt ? `Since ${formatDate(teams.chatLinkedAt)}` : null;
    else if (!teams.microsoftAccount) chatNote = "They don't sign in with Microsoft";
    else chatNote = 'Starts when they add the Team Today app in Teams';

    body = (
      <Card.Body>
        <Row className="g-4">
          <Col lg={6}>
            <h6 className="mb-3 text-secondary">Email</h6>
            {suppressionText && (
              <p className="small mb-3" style={{ color: 'var(--color-attention-fg)' }}>
                {suppressionText}
                {suppression.detail && <span className="d-block text-muted">{suppression.detail}</span>}
              </p>
            )}
            {!email.suppressionChecked && (
              <p className="small text-muted mb-3">Couldn&apos;t check whether this address is blocked.</p>
            )}
            <NotificationList items={emailItems} />
          </Col>
          <Col lg={6}>
            <h6 className="mb-3 text-secondary">Microsoft Teams</h6>
            <NotificationList items={[
              { label: 'Team Today app chat', on: teams.chatLinked, labels: ['Linked', 'Not linked'], note: chatNote },
              { label: 'Teams notifications', on: !teams.notificationsDisabled, note: teams.notificationsDisabled ? 'Switched off for this user' : null },
              ...teamsItems,
            ]} />
          </Col>
        </Row>
      </Card.Body>
    );
  }

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="bg-light d-flex flex-wrap align-items-center justify-content-between gap-2">
        <h5 className="mb-0">Notifications</h5>
        {headerNote && <span className="small text-muted">{headerNote}</span>}
      </Card.Header>
      {body}
    </Card>
  );
};

export default UserNotifications;
